<template>
  <div class="eu-pb_5">
    <div class="emd-container _highlight_header">
      <div class="emd-container__header">
        <h2 class="emd-text _size_default _type_caption">
          {{ $t('title') }}
        </h2>
      </div>
      <div class="emd-container__content">
        <div class="eu-py_3 _grid-receipt-resume">
          <Transaction-summary-item
            :text="authorizedText"
            :value="formatCurrency(charge.graphics_data.authorized.value / 100)"
            sign="+"
          />

          <Transaction-summary-item
            :text="capturedText"
            :value="formatCurrency(charge.graphics_data.captured.value / 100)"
            sign="+"
            classColor="_color_success"
          />
          <Transaction-summary-item
            v-if="isPSP"
            :value="formatCurrency(discounts / 100)"
            sign="-"
            classColor="_color_warning"
          >
            <template slot="tippy">
              <p class="emd-text _color_warning eu-mt_2 eu-ws_nowrap">
                {{ $t('discounts') }}
                <emd-tippy
                  v-if="hasDiscount"
                  id="discounts-tippy"
                  class="eu-ml_1"
                  :type="'simple'"
                  :position="'bottom'"
                  :content="discountsDetails"
                >
                  <i class="emd-text _color_warning uil uil-info-circle"></i>
                </emd-tippy>
              </p>
            </template>
          </Transaction-summary-item>

          <Transaction-summary-item
            v-if="isPSP"
            :value="cost"
            sign="-"
          >
            <template slot="tippy">
              <p class="emd-text eu-mt_2 eu-ws_nowrap">
                {{ $t('cost') }}
                <emd-tippy
                  id="cost-tippy"
                  class="eu-ml_1"
                  :type="'simple'"
                  :position="'bottom'"
                  :content="$t('cost-detail')"
                >
                  <i class="uil uil-info-circle"></i>
                </emd-tippy>
              </p>
            </template>
          </Transaction-summary-item>

          <Transaction-summary-item
            :text="canceledText"
            :value="formatCurrency(charge.graphics_data.canceled.value / 100)"
            sign="-"
            classColor="_color_danger"
          />

          <Transaction-summary-item
            v-if="showNetValue"
            :value="showNetAmount"
            classColor="_color_info"
          >
            <template slot="tippy">
              <p class="emd-text eu-mt_2 _color_info eu-ws_nowrap">
                {{ isPSP ? $t('net-amount') : $t('actual-amount') }}
                <emd-tippy
                  v-if="showNetValueTippy"
                  id="actual-amount-tippy"
                  class="eu-ml_1"
                  :type="'simple'"
                  :position="'bottom'"
                  :content="$t('actual-amount-detail')"
                >
                  <i class="uil uil-info-circle _color_info"></i>
                </emd-tippy>
              </p>
            </template>
          </Transaction-summary-item>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EmdTippy } from 'emerald-vue/src/components'
import { isBankTransfer, isCash } from '@/enums/payment-method.enum'
import MathManager from '@utils/math-manager.utils'
import TransactionSummaryItem from './TransactionSummaryItem.vue'
export default {
  name: 'TransactionSummary',
  components: {
    EmdTippy,
    TransactionSummaryItem
  },
  props: {
    charge: {
      required: true,
      type: Object
    },
    isPSP: Boolean,
    isBoleto: Boolean
  },
  computed: {
    hasDiscount () {
      return !!this.charge?.graphics_data?.discounts?.length
    },
    authorizedText () {
      if (this.isBankTransfer) return 'transfer-amount'
      if (this.isCash) return 'charge-amount'
      return 'authorized-amount'
    },
    capturedText () {
      if (this.isPSP && this.hasDiscount) return 'received-amount'
      if (this.isBankTransfer || this.isBoleto) return 'paid-amount'
      if (this.isCash) return 'confirmed-amount'
      return 'captured-amount'
    },
    canceledText () {
      if (this.isPSP) return 'refund-amount'
      return 'canceled-amount'
    },
    isBankTransfer () {
      return isBankTransfer(this.charge.payment_method)
    },
    isCash () {
      return isCash(this.charge.payment_method)
    },
    discounts () {
      let discounts = 0
      if (this.hasDiscount) {
        this.charge.graphics_data.discounts.map(item => {
          discounts += MathManager.parseCurrencyToNumber(item.value) * 100
        })
      }
      return discounts
    },
    discountsDetails () {
      if (this.hasDiscount) {
        return this.charge.graphics_data.discounts
          .map(item => `${item.label}: ${item.value}`)
          .join(' | ')
      }
      return undefined
    },
    cost () {
      return this.charge.cost ? this.charge.cost : this.formatCurrency(0)
    },
    showNetValue () {
      return (!this.isBoleto && !this.isBankTransfer) || this.isPSP
    },
    showNetValueTippy () {
      return !this.isPSP || !this.hasDiscount
    },
    showNetAmount () {
      if (!(this.charge.cancel.show || this.isPSP)) return 'R$ 0,00'
      return this.netValueAmount > 0
        ? `+ ${this.formatCurrency(this.netValueAmount)}`
        : this.formatCurrency(this.netValueAmount)
    },
    netValueAmount () {
      if (this.isPSP) {
        return (
          (this.charge.graphics_data.captured.value -
            this.discounts -
            this.charge.graphics_data.canceled.value) /
          100
        )
      }
      return (
        (this.charge.graphics_data.captured.value -
          this.charge.graphics_data.canceled.value) /
        100
      )
    }
  },
  methods: {
    formatCurrency (value) {
      return MathManager.formatCurrencyLegacy(value)
    }
  }
}
</script>

<style scoped>
._grid-receipt-resume {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  text-align: center;
  gap: 12px 0;
}

@media screen and (min-width: 1366px) {
  ._grid-receipt-resume {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media screen and (min-width: 544px) {
  ._grid-receipt-resume {
    gap: 36px 0;
  }
}
</style>

<i18n>
    {
      "pt-BR": {
        "title": "Resumo do recebimento",
        "actual-amount": "Valor atual",
        "net-amount": "Valor líquido",
        "actual-amount-detail": "Valor capturado remanescente",
        "cost": "Custo de processamento",
        "cost-detail": "O custo de processamento será debitado ao final do mês, junto aos custos de processamento das demais transações do período.",
        "discounts": "Total de saídas"
      },
      "en-US": {
        "title": "Receipt resume",
        "actual-amount": "Actual amount",
        "net-amount": "Net value amount",
        "actual-amount-detail": "Remaining captured amount",
        "cost": "Processing cost",
        "cost-detail": "The processing cost will be charged at the end of the month, along with the processing costs of the others transactions of the period.",
        "discounts": "Total output"
      }
    }
</i18n>
